import React, { useState, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import axios from "axios";
import Swal from "sweetalert2";
import './updatePaper.css';

const PaperList = () => {
  const location = useLocation();
  const [papers, setPapers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [currentPage, setCurrentPage] = useState(location.state?.returnToPage || 1);
  const papersPerPage = 12;
  const navigate = useNavigate();

  useEffect(() => {
    fetchPapers();
  }, []);

  useEffect(() => {
    // Update page if returning from paper details
    if (location.state?.returnToPage) {
      setCurrentPage(location.state.returnToPage);
    }
  }, [location.state]);

  const fetchPapers = () => {
    setLoading(true);
    axios
      .get(`${process.env.REACT_APP_BACKEND_SERVER_URL}/api/v1/submit/get-papers`)
      .then((response) => setPapers(response.data.data))
      .catch((error) =>
        setError("Failed to fetch papers. Please try again later.")
      )
      .finally(() => setLoading(false));
  };

  const indexOfLastPaper = currentPage * papersPerPage;
  const indexOfFirstPaper = indexOfLastPaper - papersPerPage;
  const currentPapers = papers.slice(indexOfFirstPaper, indexOfLastPaper);
  const totalPages = Math.ceil(papers.length / papersPerPage);

  const handleDetails = (paperId, paperIndex) => {
    navigate(`/admin/paper-details/${paperId}`, {
      state: { 
        fromPage: currentPage,
        paperIndex: indexOfFirstPaper + paperIndex,
        totalPapers: papers.length
      }
    });
  };

  const handleDelete = async (paperId, index) => {
    const result = await Swal.fire({
      title: "Are you sure?",
      text: "You won't be able to revert this!",
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#5e71f6",
      cancelButtonColor: "#dc2626",
      confirmButtonText: "Yes, delete it!",
    });
  
    if (result.isConfirmed) {
      try {
        await axios.delete(
          `${process.env.REACT_APP_BACKEND_SERVER_URL}/api/v1/submit/delete-paper`,
          { data: { paperId } }
        );
        
        fetchPapers();
        Swal.fire({
          title: "Deleted!",
          text: "The paper has been deleted.",
          icon: "success",
          confirmButtonColor: "#5e71f6",
        });
  
      } catch (error) {
        console.error("Error deleting paper:", error);
        Swal.fire({
          title: "Error!",
          text: "Failed to delete the paper. Please try again.",
          icon: "error",
          confirmButtonColor: "#5e71f6",
        });
      }
    }
  };

  const handlePageChange = (pageNumber) => {
    setCurrentPage(pageNumber);
  };

  const handlePreviousPage = () => {
    if (currentPage > 1) {
      setCurrentPage(currentPage - 1);
    }
  };

  const handleNextPage = () => {
    if (currentPage < totalPages) {
      setCurrentPage(currentPage + 1);
    }
  };

  if (loading) {
    return (
      <div className="update-paper-list-wrapper">
        <div className="loading-container">
          <div className="loading-spinner"></div>
          <p>Loading papers...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="update-paper-list-wrapper">
        <div className="error-container">
          <p className="error-message">{error}</p>
          <button onClick={fetchPapers} className="retry-button">
            Retry
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="update-paper-list-wrapper">
      <div className="update-paper-list-container">
        {/* Header Section */}
        <div className="update-header-section">
          <h1 className="update-paper-list-title">All Papers</h1>
          <p className="update-paper-list-subtitle">
            Manage and review all submitted papers
          </p>
        </div>

        {/* Papers List */}
        {currentPapers.length === 0 ? (
          <div className="no-papers">
            <p>No papers found</p>
          </div>
        ) : (
          <div className="update-papers-grid">
            {currentPapers.map((paper, index) => (
              <div key={paper._id} className="update-paper-card">
                <div className="update-paper-header">
                  <span className="update-paper-id-badge">Paper ID: {paper.paperID}</span>
                </div>
                <h2 className="update-paper-title">{paper.title}</h2>
                <div className="update-paper-actions">
                  <button
                    className="update-details-button"
                    onClick={() => handleDetails(paper._id, index)}
                  >
                    <svg
                      className="button-icon"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"
                      />
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z"
                      />
                    </svg>
                    View Details
                  </button>
                  <button
                    className="delete-button"
                    onClick={() => handleDelete(paper._id, index)}
                  >
                    <svg
                      className="button-icon"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"
                      />
                    </svg>
                    Delete
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Pagination */}
        {totalPages > 1 && (
          <div className="pagination">
            <button
              onClick={handlePreviousPage}
              disabled={currentPage === 1}
              className="pagination-nav-button"
            >
              <svg
                className="pagination-icon"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M15 19l-7-7 7-7"
                />
              </svg>
              Previous
            </button>
            
            <div className="pagination-numbers">
              {Array.from({ length: totalPages }, (_, index) => {
                const pageNum = index + 1;
                // Show first, last, current, and pages around current
                if (
                  pageNum === 1 ||
                  pageNum === totalPages ||
                  (pageNum >= currentPage - 1 && pageNum <= currentPage + 1)
                ) {
                  return (
                    <button
                      key={index}
                      onClick={() => handlePageChange(pageNum)}
                      className={`pagination-number ${
                        currentPage === pageNum ? "active" : ""
                      }`}
                    >
                      {pageNum}
                    </button>
                  );
                } else if (
                  pageNum === currentPage - 2 ||
                  pageNum === currentPage + 2
                ) {
                  return <span key={index} className="pagination-ellipsis">...</span>;
                }
                return null;
              })}
            </div>

            <button
              onClick={handleNextPage}
              disabled={currentPage === totalPages}
              className="pagination-nav-button"
            >
              Next
              <svg
                className="pagination-icon"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M9 5l7 7-7 7"
                />
              </svg>
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default PaperList;
